import { useContext, useCallback, useRef } from 'react';

import { API_BASE_URL, API_ENDPOINTS, getHeaders, IS_DEBUG } from '../config/api';
import AuthContext, { isTokenExpired, ACCOUNT_DEACTIVATED_CODE } from '../store/auth-context';
import { apiErrorFromEnvelope } from '../utils/api-error';

import {
  BoxStatusCode,
  Order,
  OrderBox,
  OrderCoordinates,
  Warehouse,
  EnhancedOrderBox,
  WorkflowType,
  PartnerLoginRequest,
  PartnerLoginResponse,
  PartnerUser,
  PartnerUsersResponse,
  CourierRoutesResponse,
  WarehouseIncomingResponse,
  WarehouseOutgoingResponse,
  DpdShipmentSummary,
  StatusUpdateRequest,
  StatusUpdateResponse,
  SignatureRequest,
  BatchSyncResponse,
  STATUS_CODE_ORDER,
  ACTIONABLE_STATUSES,
  TARGET_STATUS,
  canTransitionTo,
  resolveBoxStatusCode,
  BoxStatus,
  QueuedEvent,
  SignatureQueuePayload,
  AddBoxQueuePayload,
  RemoveBoxQueuePayload,
  RouteOrderQueuePayload,
  OrderBoxResponse,
  AddBoxToOrderResponse,
  RemoveBoxFromOrderResponse,
  PhotoUploadRequest,
  PhotoUploadResponse,
  DriverAssignmentRequest,
} from '../types/logistics';
import { putBlob } from '../utils/offline-blob-store';

import type { OCRQueuePayload, OCRBatchSyncResponse } from '../types/ocr';
import {
  getStatusOrder,
  getStatusCodeForAction,
  calcProgress,
} from '../utils/box-helpers';

// ===========================================
// Offline Queue Management
// ===========================================

const OFFLINE_QUEUE_KEY = 'logistics_offline_queue';
const OFFLINE_FAILURES_KEY = 'logistics_offline_failures';
export const OFFLINE_QUEUE_CHANGED_EVENT = 'offline-queue-changed';

export const getOfflineQueue = (): QueuedEvent[] => {
  const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
  return stored ? JSON.parse(stored) : [];
};

export const saveOfflineQueue = (queue: QueuedEvent[]) => {
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  } catch (err) {
    // QuotaExceededError: evict oldest low-priority events and retry once
    if (err instanceof DOMException && err.name === 'QuotaExceededError') {
      const trimmed = [...queue];
      const priority = (t: QueuedEvent['type']) => {
        if (t === 'ROUTE_ORDER') return 0;
        if (t === 'OCR_REQUEST') return 1;
        return 2;
      };
      trimmed.sort((a, b) => priority(a.type) - priority(b.type) || a.createdAt - b.createdAt);
      while (trimmed.length > 0) {
        trimmed.shift();
        try {
          localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(trimmed));
          console.warn('[Offline] Queue trimmed due to quota; dropped oldest low-priority event');
          break;
        } catch {
          // keep trimming
        }
      }
    } else {
      throw err;
    }
  }
  try {
    window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_CHANGED_EVENT));
  } catch {
    // non-browser env (tests) — safe to ignore
  }
};

export const getOfflineFailures = (): QueuedEvent[] => {
  const stored = localStorage.getItem(OFFLINE_FAILURES_KEY);
  return stored ? JSON.parse(stored) : [];
};

export const saveOfflineFailures = (failures: QueuedEvent[]) => {
  try {
    localStorage.setItem(OFFLINE_FAILURES_KEY, JSON.stringify(failures));
  } catch {
    // swallow — failures log is best-effort
  }
};

export const recordFailure = (event: QueuedEvent) => {
  const failures = getOfflineFailures();
  failures.push(event);
  // Cap failure log at 100 entries
  while (failures.length > 100) failures.shift();
  saveOfflineFailures(failures);
};

const generateEventId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/** Negative temp ids never collide with server (positive) ids. Kept strictly monotonic. */
let tempIdCounter = 0;
export const generateTempBoxId = (): number => {
  tempIdCounter += 1;
  // Always negative, always unique per session
  return -(Date.now() * 1000 + tempIdCounter);
};

// ===========================================
// Transform Functions (API → Enhanced Format)
// ===========================================

/**
 * Converts API OrderBox to EnhancedOrderBox with resolved status code.
 * Uses resolveBoxStatusCode() to handle both currentStatusId and currentStatus.code.
 * 
 * This replaces the old transform approach that incorrectly
 * defaulted missing statuses to WCAC.
 */
const transformOrderBoxToEnhanced = (orderBox: OrderBox): EnhancedOrderBox => {
  // Use the centralized resolver that handles both ID and code formats
  const statusCode = resolveBoxStatusCode(orderBox);
  
  return {
    ...orderBox,
    barcode: orderBox.barcode || '',
    statusCode,
    isActionable: false, // Will be set by filterBoxesByWorkflow()
    isCompleted: false,  // Will be set by filterBoxesByWorkflow()
  };
};

/**
 * Filters and marks boxes based on workflow context.
 * Sets isActionable and isCompleted flags for each box.
 * @param boxes - Array of enhanced order boxes
 * @param workflowType - The workflow context (collection, delivery, etc.)
 * @returns Updated boxes with actionable/completed flags set
 */
const filterBoxesByWorkflow = <T extends { statusCode: BoxStatusCode; isActionable: boolean; isCompleted: boolean }>(
  boxes: T[], 
  workflowType: WorkflowType
): T[] => {
  const actionableStatuses = ACTIONABLE_STATUSES[workflowType] || [];
  const targetStatus = TARGET_STATUS[workflowType];
  const targetStatusOrder = targetStatus ? STATUS_CODE_ORDER[targetStatus] : -1;

  return boxes.map(box => ({
    ...box,
    isActionable: actionableStatuses.includes(box.statusCode),
    isCompleted: targetStatusOrder >= 0 && STATUS_CODE_ORDER[box.statusCode] >= targetStatusOrder,
  }));
};

/**
 * Gets only boxes that are actionable for a given workflow.
 */
const getActionableBoxes = <T extends { statusCode: BoxStatusCode; isActionable: boolean; isCompleted: boolean }>(
  boxes: T[], 
  workflowType: WorkflowType
): T[] => {
  return filterBoxesByWorkflow(boxes, workflowType).filter(box => box.isActionable || box.isCompleted);
};

// ===========================================
// Exported Transform Functions
// ===========================================

/**
 * Converts API OrderBox to EnhancedOrderBox with resolved status code.
 * Exported for use in components that need to process boxes.
 */
export { transformOrderBoxToEnhanced };

// ===========================================
// Route Result Type
// ===========================================

export interface CourierRoutesResult {
  orders: Order[];
  orderCoordinates: Record<number, OrderCoordinates> | null;
  warehouse: Warehouse | null;
  stopOrder: string[] | null;
}

export interface WarehouseOutgoingResult {
  orders: Order[];
  dpdShipments: Record<number, DpdShipmentSummary>;
}

// ===========================================
// Main Hook
// ===========================================

const useLogisticsApi = () => {
  const authCtx = useContext(AuthContext);
  const syncInProgress = useRef(false);

  // Get JWT token from auth context
  const getToken = useCallback((): string | null => {
    const user = authCtx.user as any;
    return user?.jwt || null;
  }, [authCtx.user]);

  // ===========================================
  // Base Request Function
  // ===========================================

  const request = useCallback(async <T>(
    endpoint: string,
    options: RequestInit = {},
    requiresAuth = true
  ): Promise<T> => {
    const token = getToken();
    
    if (requiresAuth && !token) {
      throw new Error('Not authenticated');
    }

    // Client-side expiry check — avoid sending requests with known-expired tokens
    if (requiresAuth && token && isTokenExpired(token)) {
      localStorage.removeItem('user');
      localStorage.removeItem('partner');
      authCtx.updateUser(null);
      authCtx.updatePartner(null);
      throw new Error('Session expired. Please login again.');
    }

    const url = `${API_BASE_URL}${endpoint}`;
    const headers = getHeaders(requiresAuth ? token || undefined : undefined);

    if (IS_DEBUG) {
      console.log(`[API] ${options.method || 'GET'} ${url}`, options.body ? JSON.parse(options.body as string) : '');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      }
    });

    const data = await response.json();

    if (IS_DEBUG) {
      console.log(`[API] Response:`, data);
    }

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired - clear both localStorage and React state
        localStorage.removeItem('user');
        localStorage.removeItem('partner');
        authCtx.updateUser(null);
        authCtx.updatePartner(null);
        throw new Error('Session expired. Please login again.');
      }
      // A deactivation can land mid-shift, and the token keeps working until it
      // expires. Without this the courier just watches each scan fail. End the
      // session and carry the reason to the login screen - unlike a 401, signing
      // back in is not going to help, so they need to be told why.
      if (response.status === 403 && data?.code === ACCOUNT_DEACTIVATED_CODE) {
        const message =
          data.error ||
          'This account has been deactivated. Please contact Kit Keeper operations.';
        authCtx.endSessionAsDeactivated(message);
        throw new Error(message);
      }
      throw apiErrorFromEnvelope(response.status, data);
    }

    return data;
  }, [getToken, authCtx]);

  // ===========================================
  // Authentication
  // ===========================================

  const login = useCallback(async (credentials: PartnerLoginRequest): Promise<PartnerLoginResponse> => {
    const response = await request<PartnerLoginResponse>(
      API_ENDPOINTS.LOGIN,
      {
        method: 'POST',
        body: JSON.stringify(credentials)
      },
      false // Don't require auth for login
    );
    return response;
  }, [request]);

  const getCurrentUser = useCallback(async () => {
    return request(API_ENDPOINTS.ME);
  }, [request]);

  // ===========================================
  // Offline Queue Operations
  // ===========================================

  const queueStatusUpdate = useCallback((payload: StatusUpdateRequest) => {
    const queue = getOfflineQueue();
    const event: QueuedEvent = {
      id: generateEventId(),
      type: 'STATUS_CHANGE',
      payload,
      createdAt: Date.now(),
      attempts: 0,
      orderId: undefined,
      tempBoxId: payload.orderBoxId < 0 ? payload.orderBoxId : undefined,
    };
    queue.push(event);
    saveOfflineQueue(queue);

    if (IS_DEBUG) {
      console.log('[Offline] Queued status update:', event);
    }
  }, []);

  const queueSignature = useCallback(async (
    orderId: number,
    isCollection: boolean,
    signatureDataUrl: string,
    proofPhotoBase64: string | undefined
  ): Promise<void> => {
    const eventId = generateEventId();
    const blobRefs: string[] = [];

    const signatureBlobRef = `${eventId}-signature`;
    await putBlob(signatureBlobRef, signatureDataUrl);
    blobRefs.push(signatureBlobRef);

    let proofPhotoBlobRef: string | undefined;
    if (proofPhotoBase64) {
      proofPhotoBlobRef = `${eventId}-proof`;
      await putBlob(proofPhotoBlobRef, proofPhotoBase64);
      blobRefs.push(proofPhotoBlobRef);
    }

    const payload: SignatureQueuePayload = {
      orderId,
      isCollection,
      signatureBlobRef,
      proofPhotoBlobRef,
      clientTimestamp: Date.now(),
    };

    // Coalesce: only one signature per (orderId, isCollection) makes sense — drop older duplicates
    const queue = getOfflineQueue().filter((e) => {
      if (e.type !== 'SIGNATURE') return true;
      const p = e.payload as SignatureQueuePayload;
      return !(p.orderId === orderId && p.isCollection === isCollection);
    });

    queue.push({
      id: eventId,
      type: 'SIGNATURE',
      payload,
      createdAt: Date.now(),
      attempts: 0,
      orderId,
      blobRefs,
    });
    saveOfflineQueue(queue);

    if (IS_DEBUG) console.log('[Offline] Queued signature:', eventId);
  }, []);

  const queueAddBox = useCallback((
    orderId: number,
    tempBoxId: number,
    opts: { barcode?: string; notes?: string; latitude?: number; longitude?: number }
  ): void => {
    const payload: AddBoxQueuePayload = {
      orderId,
      tempBoxId,
      barcode: opts.barcode,
      notes: opts.notes,
      latitude: opts.latitude,
      longitude: opts.longitude,
      clientTimestamp: Date.now(),
    };
    const queue = getOfflineQueue();
    queue.push({
      id: generateEventId(),
      type: 'ADD_BOX',
      payload,
      createdAt: Date.now(),
      attempts: 0,
      orderId,
      tempBoxId,
    });
    saveOfflineQueue(queue);
    if (IS_DEBUG) console.log('[Offline] Queued add-box:', tempBoxId);
  }, []);

  const queueRemoveBox = useCallback((
    orderId: number,
    orderBoxId: number,
    opts: { reason?: string; latitude?: number; longitude?: number }
  ): void => {
    // Special case: removing a box that was added offline in the same session
    // (its ADD_BOX event hasn't synced yet). Collapse add+remove+any scans to a no-op.
    if (orderBoxId < 0) {
      const queue = getOfflineQueue().filter((e) => {
        if (e.type === 'ADD_BOX') {
          return (e.payload as AddBoxQueuePayload).tempBoxId !== orderBoxId;
        }
        if (e.type === 'STATUS_CHANGE') {
          return (e.payload as StatusUpdateRequest).orderBoxId !== orderBoxId;
        }
        return true;
      });
      saveOfflineQueue(queue);
      if (IS_DEBUG) console.log('[Offline] Collapsed add+remove for temp box', orderBoxId);
      return;
    }

    const payload: RemoveBoxQueuePayload = {
      orderId,
      orderBoxId,
      reason: opts.reason,
      latitude: opts.latitude,
      longitude: opts.longitude,
      clientTimestamp: Date.now(),
    };
    const queue = getOfflineQueue();
    queue.push({
      id: generateEventId(),
      type: 'REMOVE_BOX',
      payload,
      createdAt: Date.now(),
      attempts: 0,
      orderId,
    });
    saveOfflineQueue(queue);
    if (IS_DEBUG) console.log('[Offline] Queued remove-box:', orderBoxId);
  }, []);

  const queueRouteOrder = useCallback((date: string, stopOrder: string[]): void => {
    const payload: RouteOrderQueuePayload = {
      date,
      stopOrder,
      clientTimestamp: Date.now(),
    };
    // Last-write-wins: drop any older ROUTE_ORDER for the same date
    const queue = getOfflineQueue().filter((e) => {
      if (e.type !== 'ROUTE_ORDER') return true;
      return (e.payload as RouteOrderQueuePayload).date !== date;
    });
    queue.push({
      id: generateEventId(),
      type: 'ROUTE_ORDER',
      payload,
      createdAt: Date.now(),
      attempts: 0,
    });
    saveOfflineQueue(queue);
    if (IS_DEBUG) console.log('[Offline] Queued route order:', date);
  }, []);

  // ===========================================
  // Route Operations
  // ===========================================

  const getCourierRoutes = useCallback(async (date: string): Promise<CourierRoutesResult> => {
    const response = await request<CourierRoutesResponse>(
      API_ENDPOINTS.COURIER_ROUTES(date)
    );
    return {
      orders: response.orders,
      orderCoordinates: response.orderCoordinates ?? null,
      warehouse: response.warehouse ?? null,
      stopOrder: response.stopOrder ?? null,
    };
  }, [request]);

  const saveRouteOrder = useCallback(async (date: string, stopOrder: string[]): Promise<void> => {
    if (!navigator.onLine) {
      queueRouteOrder(date, stopOrder);
      return;
    }
    await request(
      API_ENDPOINTS.COURIER_ROUTE_ORDER(date),
      {
        method: 'PUT',
        body: JSON.stringify({ stopOrder })
      }
    );
  }, [request, queueRouteOrder]);

  const getWarehouseIncoming = useCallback(async (date: string): Promise<Order[]> => {
    const response = await request<WarehouseIncomingResponse>(
      API_ENDPOINTS.WAREHOUSE_INCOMING(date)
    );
    return response.orders;
  }, [request]);

  const getWarehouseOutgoing = useCallback(async (date: string): Promise<WarehouseOutgoingResult> => {
    const response = await request<WarehouseOutgoingResponse>(
      API_ENDPOINTS.WAREHOUSE_OUTGOING(date)
    );
    return {
      orders: response.orders,
      dpdShipments: response.dpdShipments ?? {},
    };
  }, [request]);

  // ===========================================
  // Box Operations
  // ===========================================

  const getBoxByBarcode = useCallback(async (barcode: string): Promise<OrderBox> => {
    const response = await request<OrderBoxResponse>(
      API_ENDPOINTS.ORDER_BOX(barcode)
    );
    return response.orderBox;
  }, [request]);

  const updateBoxStatus = useCallback(async (
    orderBoxId: number,
    newStatusCode: BoxStatusCode,
    options?: {
      latitude?: number;
      longitude?: number;
      warehouseLocation?: string;
      notes?: string;
      photoUrl?: string;
      photoBase64?: string;
    }
  ): Promise<StatusUpdateResponse> => {
    const payload: StatusUpdateRequest = {
      orderBoxId,
      newStatusCode,
      ...options,
      clientTimestamp: Date.now()
    };

    // If offline, queue the update
    if (!navigator.onLine) {
      queueStatusUpdate(payload);
      return {
        statusCode: '202 Queued',
        historyEntry: {
          id: -1,
          orderBoxId,
          orderId: 0,
          customerId: 0,
          previousStatusId: null,
          newStatusId: 0,
          triggeredByUserId: null,
          triggeredByRole: null,
          triggeredByPartnerId: null,
          latitude: options?.latitude || null,
          longitude: options?.longitude || null,
          warehouseLocation: options?.warehouseLocation || null,
          notes: options?.notes || null,
          photoUrl: options?.photoUrl || null,
          signatureUrl: null,
          createdAt: Date.now(),
          clientTimestamp: payload.clientTimestamp || null,
          syncedAt: null
        }
      };
    }

    return request<StatusUpdateResponse>(
      API_ENDPOINTS.ORDER_BOX_STATUS(orderBoxId),
      {
        method: 'PUT',
        body: JSON.stringify(payload)
      }
    );
  }, [request, queueStatusUpdate]);

  // ===========================================
  // Signature Operations
  // ===========================================

  const submitSignature = useCallback(async (
    orderId: number,
    signatureUrl: string,
    isCollection: boolean,
    proofPhotoBase64?: string
  ): Promise<void> => {
    if (!navigator.onLine) {
      await queueSignature(orderId, isCollection, signatureUrl, proofPhotoBase64);
      return;
    }

    const payload: SignatureRequest = {
      signatureUrl,
      isCollection,
      ...(proofPhotoBase64 ? { proofPhotoBase64 } : {})
    };

    await request(
      API_ENDPOINTS.ORDER_SIGNATURE(orderId),
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    );
  }, [request, queueSignature]);

  const uploadPhoto = useCallback(async (
    imageBase64: string,
    orderId?: number
  ): Promise<string> => {
    const payload: PhotoUploadRequest = { imageBase64, orderId };
    const response = await request<PhotoUploadResponse>(
      API_ENDPOINTS.PHOTO_UPLOAD,
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    );
    return response.photoUrl;
  }, [request]);

  // ===========================================
  // Box Management (Add/Remove during collection)
  // ===========================================

  const addBoxToOrder = useCallback(async (
    orderId: number,
    options?: { barcode?: string; notes?: string; latitude?: number; longitude?: number }
  ): Promise<OrderBox> => {
    if (!navigator.onLine) {
      const tempBoxId = generateTempBoxId();
      queueAddBox(orderId, tempBoxId, options || {});
      // Synthesize an OrderBox the caller can immediately render and act upon.
      // currentStatusId=1 ('WCAC' — awaiting collection) matches a freshly-added box.
      const synthetic: OrderBox = {
        id: tempBoxId,
        orderId,
        barcode: options?.barcode ?? null,
        storageCost: 0,
        shippingCost: 0,
        purchaseCost: 0,
        currentStatusId: 1,
        warehouseLocation: null,
        lastStatusUpdate: Date.now(),
        currentStatus: null,
      };
      return synthetic;
    }
    const response = await request<AddBoxToOrderResponse>(
      API_ENDPOINTS.ORDER_ADD_BOX(orderId),
      {
        method: 'POST',
        body: JSON.stringify(options || {})
      }
    );
    return response.orderBox;
  }, [request, queueAddBox]);

  const removeBoxFromOrder = useCallback(async (
    orderId: number,
    orderBoxId: number,
    options?: { reason?: string; latitude?: number; longitude?: number }
  ): Promise<void> => {
    // Temp boxes (negative id) were never synced — collapse the queue and skip the API call.
    if (!navigator.onLine || orderBoxId < 0) {
      queueRemoveBox(orderId, orderBoxId, options || {});
      return;
    }
    await request<RemoveBoxFromOrderResponse>(
      API_ENDPOINTS.ORDER_REMOVE_BOX(orderId, orderBoxId),
      {
        method: 'DELETE',
        body: JSON.stringify(options || {})
      }
    );
  }, [request, queueRemoveBox]);

  // ===========================================
  // Driver Assignment
  // ===========================================

  const assignDriver = useCallback(async (
    orderId: number,
    assignment: DriverAssignmentRequest
  ): Promise<void> => {
    await request(
      API_ENDPOINTS.ORDER_DRIVER(orderId),
      {
        method: 'PUT',
        body: JSON.stringify(assignment)
      }
    );
  }, [request]);

  // ===========================================
  // Reference Data
  // ===========================================

  const getStatuses = useCallback(async (): Promise<BoxStatus[]> => {
    const response = await request<{ statusCode: string; statuses: BoxStatus[] }>(
      API_ENDPOINTS.STATUSES
    );
    return response.statuses;
  }, [request]);

  const getPartnerUsers = useCallback(async (partnerId: number): Promise<PartnerUser[]> => {
    const response = await request<PartnerUsersResponse>(
      API_ENDPOINTS.PARTNER_USERS(partnerId)
    );
    return response.users;
  }, [request]);

  const syncOfflineQueue = useCallback(async (): Promise<BatchSyncResponse | null> => {
    if (syncInProgress.current) return null;
    if (!navigator.onLine) return null;

    const queue = getOfflineQueue();
    if (queue.length === 0) return null;

    // Don't attempt sync with an expired token
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      if (IS_DEBUG) {
        console.log('[Offline] Sync skipped — token expired or missing');
      }
      return null;
    }

    syncInProgress.current = true;

    try {
      // Separate events by type
      const statusEvents = queue
        .filter(e => e.type === 'STATUS_CHANGE')
        .map(e => e.payload as StatusUpdateRequest);

      const ocrEvents = queue
        .filter(e => e.type === 'OCR_REQUEST')
        .map(e => e.payload as OCRQueuePayload);

      let statusResponse: BatchSyncResponse | null = null;
      let ocrResponse: OCRBatchSyncResponse | null = null;

      // Sync status events
      if (statusEvents.length > 0) {
        statusResponse = await request<BatchSyncResponse>(
          API_ENDPOINTS.BATCH_SYNC,
          {
            method: 'POST',
            body: JSON.stringify({ events: statusEvents })
          }
        );
      }

      // Sync OCR events (if any)
      if (ocrEvents.length > 0) {
        try {
          ocrResponse = await request<OCRBatchSyncResponse>(
            API_ENDPOINTS.OCR_BATCH_SYNC,
            {
              method: 'POST',
              body: JSON.stringify({ requests: ocrEvents })
            }
          );
        } catch (error) {
          if (IS_DEBUG) {
            console.warn('[Offline] OCR batch sync failed:', error);
          }
          // OCR sync failure shouldn't block status sync
        }
      }

      // Remove successful status events from queue
      const failedStatusIds = new Set(
        statusResponse?.failed.map(f => f.event.orderBoxId) || []
      );

      const remainingQueue = queue.filter(e => {
        if (e.type === 'STATUS_CHANGE') {
          const payload = e.payload as StatusUpdateRequest;
          return failedStatusIds.has(payload.orderBoxId);
        }
        // Remove OCR events if sync was attempted (success or fail, we don't retry)
        if (e.type === 'OCR_REQUEST' && ocrResponse) {
          return false;
        }
        return true;
      });

      // Update retry counts for failed status events
      remainingQueue.forEach(event => {
        if (event.type === 'STATUS_CHANGE') {
          const payload = event.payload as StatusUpdateRequest;
          const failed = statusResponse?.failed.find(f => f.event.orderBoxId === payload.orderBoxId);
          if (failed) {
            event.attempts++;
            event.lastError = failed.error;
          }
        }
      });

      saveOfflineQueue(remainingQueue);

      if (IS_DEBUG) {
        console.log('[Offline] Sync complete:', { statusResponse, ocrResponse });
      }

      return statusResponse;
    } finally {
      syncInProgress.current = false;
    }
  }, [request, getToken]);

  const getQueuedEventsCount = useCallback((): number => {
    return getOfflineQueue().length;
  }, []);

  const getQueuedEventsBreakdown = useCallback(() => {
    const queue = getOfflineQueue();
    const counts = {
      status: 0,
      signature: 0,
      addBox: 0,
      removeBox: 0,
      routeOrder: 0,
      ocr: 0,
      total: queue.length,
    };
    queue.forEach((e) => {
      if (e.type === 'STATUS_CHANGE') counts.status++;
      else if (e.type === 'SIGNATURE') counts.signature++;
      else if (e.type === 'ADD_BOX') counts.addBox++;
      else if (e.type === 'REMOVE_BOX') counts.removeBox++;
      else if (e.type === 'ROUTE_ORDER') counts.routeOrder++;
      else if (e.type === 'OCR_REQUEST') counts.ocr++;
    });
    return counts;
  }, []);

  /** Returns the set of box ids that have at least one queued mutation pending sync. */
  const getPendingSyncBoxIds = useCallback((): Set<number> => {
    const ids = new Set<number>();
    getOfflineQueue().forEach((e) => {
      if (e.type === 'STATUS_CHANGE') {
        ids.add((e.payload as StatusUpdateRequest).orderBoxId);
      } else if (e.type === 'ADD_BOX') {
        ids.add((e.payload as AddBoxQueuePayload).tempBoxId);
      } else if (e.type === 'REMOVE_BOX') {
        ids.add((e.payload as RemoveBoxQueuePayload).orderBoxId);
      }
    });
    return ids;
  }, []);

  // ===========================================
  // Status Helper Functions
  // ===========================================

  /** Gets numeric sort order for a BoxStatusCode. */
  const getStatusOrderCb = useCallback((statusCode: BoxStatusCode): number => {
    return getStatusOrder(statusCode);
  }, []);

  /** Checks whether a status transition is valid. */
  const checkCanTransition = useCallback((currentCode: BoxStatusCode, targetCode: BoxStatusCode): boolean => {
    return canTransitionTo(currentCode, targetCode);
  }, []);

  /** Applies workflow filtering to boxes (sets isActionable / isCompleted flags). */
  const applyWorkflowFilter = useCallback((boxes: EnhancedOrderBox[], workflowType: WorkflowType): EnhancedOrderBox[] => {
    return filterBoxesByWorkflow(boxes, workflowType);
  }, []);

  /** Returns boxes relevant for a given workflow (actionable or completed). */
  const getWorkflowBoxes = useCallback((boxes: EnhancedOrderBox[], workflowType: WorkflowType): EnhancedOrderBox[] => {
    return getActionableBoxes(boxes, workflowType);
  }, []);

  /** Calculates progress for an order or warehouse operation. Delegates to box-helpers. */
  const calcProgressCb = useCallback((type: string, data: { boxes?: EnhancedOrderBox[]; type?: string }): number => {
    return calcProgress(type, data);
  }, []);

  /** Maps a workflow action name to the target BoxStatusCode. Delegates to box-helpers. */
  const getStatusCodeForActionCb = useCallback((action: string, location?: string): BoxStatusCode => {
    return getStatusCodeForAction(action, location);
  }, []);

  return {
    // Authentication
    login,
    getCurrentUser,
    
    // Routes
    getCourierRoutes,
    saveRouteOrder,
    getWarehouseIncoming,
    getWarehouseOutgoing,
    
    // Box Operations
    getBoxByBarcode,
    updateBoxStatus,
    addBoxToOrder,
    removeBoxFromOrder,
    
    // Signatures & Photos
    submitSignature,
    uploadPhoto,
    
    // Driver Assignment
    assignDriver,
    
    // Reference Data
    getStatuses,
    getPartnerUsers,

    // Offline Support
    syncOfflineQueue,
    getQueuedEventsCount,
    getQueuedEventsBreakdown,
    getPendingSyncBoxIds,
    
    // Status & Progress Helpers
    calcProgress: calcProgressCb,
    getStatusOrder: getStatusOrderCb,
    getStatusCodeForAction: getStatusCodeForActionCb,
    
    // Status-Based Workflow Helpers
    checkCanTransition,
    applyWorkflowFilter,
    getWorkflowBoxes,
    
    // Raw request for custom endpoints
    request
  };
};

export default useLogisticsApi;
